import React, { useMemo } from 'react';
import {
  BIOME_HEIGHT_PX,
  WORLD_PANEL_WIDTH_PX,
  type Biome,
} from '@goodtime/shared';
import { GroundDetail } from './GroundDetail';
import { Motif } from './Motif';
import { PaintDefs } from './PaintDefs';

/**
 * Lookup of commissioned full-bleed hero scene PNGs by biome id.
 * Uses Vite's `import.meta.glob` so biomes without a scene file
 * gracefully fall back to procedural sky + silhouettes (instead of
 * a build-time import error). When a biome's `<biomeId>.png` is added
 * to apps/mobile/src/assets/map_scene/, it's automatically picked up.
 */
const SCENE_MODULES = import.meta.glob<{ default: string }>(
  '../../assets/map_scene/*.png',
  { eager: true, query: '?url' },
);
const SCENE_BY_BIOME: Partial<Record<Biome['id'], string>> = Object.fromEntries(
  Object.entries(SCENE_MODULES)
    .map(([path, mod]) => {
      const id = path.match(/([^/]+)\.png$/)?.[1] ?? null;
      return id ? [id, mod.default] : null;
    })
    .filter((entry): entry is [string, string] => entry !== null),
);

export interface BiomeProps {
  biome: Biome;
  /** 0..N-1 index inside BIOMES — used to key gradient ids. */
  idx: number;
  /** Biome immediately above — used to feather sky gradient at top. */
  prevBiome: Biome | null;
  /** Biome immediately below — used to spawn a transition motif near
   * the bottom seam, biased toward the next biome's voice. */
  nextBiome: Biome | null;
}

interface SpriteEntry {
  kind: string;
  xFrac: number;
  yFrac: number;
  scale: number;
  opacity?: number;
}

interface TuftEntry {
  xFrac: number;
  yFrac: number;
  scale: number;
  variant: number;
}

interface BiomeLayout {
  skyTop: string;
  skyBottom: string;
  rand: (i: number) => number;
  heroMotifs: SpriteEntry[];
  fillerMotifs: SpriteEntry[];
  transitionMotif: SpriteEntry | null;
  tufts: TuftEntry[];
}

/**
 * Shared layout math for a biome — sky stops, deterministic motif
 * placement, tuft scatter. Used by both {@link BiomeBackground} (which
 * draws the sky + silhouettes only) and {@link BiomeForeground} (which
 * draws the motifs + tufts on top of the path).
 */
function useBiomeLayout({ biome, prevBiome, nextBiome }: BiomeProps): BiomeLayout {
  const skyTop = prevBiome
    ? mixColors(prevBiome.sky[2], biome.sky[0], 0.5)
    : biome.sky[0];
  const skyBottom = nextBiome
    ? mixColors(biome.sky[2], nextBiome.sky[0], 0.5)
    : biome.sky[2];

  const seed = biome.id.charCodeAt(0) + biome.id.charCodeAt(1);
  const rand = (i: number): number => {
    const x = Math.sin(seed + i * 13.7) * 10000;
    return x - Math.floor(x);
  };

  // Hero motifs — one position per slot in biome.motifs (5 entries).
  // yFrac < 0 spills into top seam; yFrac > 1 into bottom seam.
  const heroMotifs = useMemo<SpriteEntry[]>(() => {
    return biome.motifs.map((kind, i) => ({
      kind,
      yFrac: -0.04 + i * 0.22,
      xFrac:
        i % 2 === 0 ? 0.1 + rand(i) * 0.08 : 0.82 - rand(i) * 0.08,
      scale: 1.05 + rand(i + 9) * 0.5,
    }));
  }, [biome.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Transition motif — extra near-bottom sprite biased toward the
  // next biome's voice, smoothing the visual handoff.
  const transitionMotif = useMemo<SpriteEntry | null>(() => {
    if (!nextBiome) return null;
    const kind = nextBiome.motifs[0];
    if (!kind) return null;
    return { kind, xFrac: 0.5, yFrac: 1.05, scale: 0.85, opacity: 0.85 };
  }, [nextBiome]);

  // Filler motifs — smaller, scattered, reused biome-specific kinds.
  const fillerKinds: Record<Biome['id'], readonly string[]> = {
    kingdom: ['tree', 'tree', 'flag'],
    cove: ['palm', 'wave', 'wave'],
    frost: ['pine', 'pine', 'peak'],
    frontier: ['cactus', 'cactus', 'mesa'],
    neon: ['sign', 'tower', 'sign'],
    egypt: ['palm', 'cactus', 'palm'],
    candy: ['wildflowers', 'wildflowers', 'flag'],
  };
  const filler = fillerKinds[biome.id] ?? ['tree'];
  const fillerMotifs = useMemo<SpriteEntry[]>(() => {
    return Array.from({ length: 14 }).map((_, i) => {
      const r = rand(i + 100);
      const r2 = rand(i + 200);
      return {
        kind: filler[i % filler.length]!,
        xFrac: r,
        yFrac: 0.4 + r2 * 0.55,
        scale: 0.45 + r * 0.35,
        opacity: 0.55 + r2 * 0.3,
      };
    });
  }, [biome.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ground tufts along bottom band; some bleed into the seam.
  const tufts = useMemo<TuftEntry[]>(() => {
    return Array.from({ length: 18 }).map((_, i) => {
      const r = rand(i + 300);
      const r2 = rand(i + 400);
      return {
        xFrac: r,
        yFrac: 0.82 + r2 * 0.22,
        scale: 0.6 + r * 0.4,
        variant: i % 3,
      };
    });
  }, [biome.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return { skyTop, skyBottom, rand, heroMotifs, fillerMotifs, transitionMotif, tufts };
}

/**
 * Background half of a biome panel — full-bleed commissioned hero
 * scene PNG when available, or procedural sky + silhouettes as a
 * fallback. Renders below the path and the foreground motifs.
 *
 * <p>Asset brief pack 3 §24 layout rework — when a biome has a
 * commissioned `map_scene/<biome>.png` painting, the procedural
 * sky/silhouettes are replaced by that painting full-bleed (the
 * painting bakes in everything: sky, mountains, foreground props,
 * atmosphere, character cameos). PvZ Pirate Seas is the visual bar.
 *
 * <p>The painting is composed with a vertical S-curve channel free
 * of major elements where the path winds through, and renders below
 * the foreground motifs (which still scatter procedural set-pieces
 * for biomes lacking a hero scene yet).
 */
export function BiomeBackground(props: BiomeProps) {
  const { biome, idx } = props;
  const { skyTop, skyBottom, rand } = useBiomeLayout(props);
  const sceneUrl = SCENE_BY_BIOME[biome.id];

  if (sceneUrl) {
    return (
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: BIOME_HEIGHT_PX,
          overflow: 'hidden',
        }}
      >
        <img
          src={sceneUrl}
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            display: 'block',
          }}
        />
      </div>
    );
  }

  // Fallback procedural composition (sky gradient + silhouettes) for
  // biomes without a commissioned hero scene yet.
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: BIOME_HEIGHT_PX,
      }}
    >
      <svg
        width="100%"
        height={BIOME_HEIGHT_PX}
        viewBox={`0 0 ${WORLD_PANEL_WIDTH_PX} ${BIOME_HEIGHT_PX}`}
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, display: 'block' }}
        aria-hidden
      >
        <defs>
          <linearGradient id={`sky-${idx}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={skyTop} />
            <stop offset="35%" stopColor={biome.sky[0]} />
            <stop offset="70%" stopColor={biome.sky[1]} />
            <stop offset="100%" stopColor={skyBottom} />
          </linearGradient>
        </defs>
        <rect
          width={WORLD_PANEL_WIDTH_PX}
          height={BIOME_HEIGHT_PX}
          fill={`url(#sky-${idx})`}
        />
      </svg>
      <SilhouetteLayer idx={idx} kind="far" biome={biome} rand={rand} />
      <SilhouetteLayer idx={idx} kind="mid" biome={biome} rand={rand} />
    </div>
  );
}

/**
 * Foreground half of a biome panel — hero + filler + transition
 * motifs and ground tufts. Rendered at higher z-index than the path
 * so trees / cottages / cacti occlude the path where they overlap.
 *
 * <p>When the biome has a commissioned hero scene PNG (which bakes
 * in foreground props), this renders nothing — the painting carries
 * the full composition.
 */
export function BiomeForeground(props: BiomeProps) {
  const { biome } = props;
  const { heroMotifs, fillerMotifs, transitionMotif, tufts } =
    useBiomeLayout(props);
  if (SCENE_BY_BIOME[biome.id]) {
    return null;
  }
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: BIOME_HEIGHT_PX,
        pointerEvents: 'none',
      }}
      aria-hidden
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          overflow: 'visible',
        }}
      >
        {fillerMotifs.map((m, i) => (
          <SpriteSlot
            key={`f${i}`}
            xPercent={m.xFrac * 100}
            yPx={m.yFrac * BIOME_HEIGHT_PX}
            scale={m.scale}
            opacity={m.opacity}
          >
            <Motif kind={m.kind} />
          </SpriteSlot>
        ))}
        {heroMotifs.map((m, i) => (
          <SpriteSlot
            key={`h${i}`}
            xPercent={m.xFrac * 100}
            yPx={m.yFrac * BIOME_HEIGHT_PX}
            scale={m.scale}
          >
            <Motif kind={m.kind} />
          </SpriteSlot>
        ))}
        {transitionMotif && (
          <SpriteSlot
            xPercent={transitionMotif.xFrac * 100}
            yPx={transitionMotif.yFrac * BIOME_HEIGHT_PX}
            scale={transitionMotif.scale}
            opacity={transitionMotif.opacity}
          >
            <Motif kind={transitionMotif.kind} />
          </SpriteSlot>
        )}
      </div>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          overflow: 'visible',
        }}
      >
        {tufts.map((t, i) => (
          <SpriteSlot
            key={`t${i}`}
            xPercent={t.xFrac * 100}
            yPx={t.yFrac * BIOME_HEIGHT_PX}
            scale={t.scale}
          >
            <GroundDetail biomeId={biome.id} variant={t.variant} />
          </SpriteSlot>
        ))}
      </div>
    </div>
  );
}

interface SpriteSlotProps {
  xPercent: number;
  yPx: number;
  scale: number;
  opacity?: number;
  children: React.ReactNode;
}

/** One motif/tuft slot — HTML-positioned at percent-x / pixel-y, with
 * a native-size inner SVG so sprite content keeps its aspect ratio
 * regardless of panel container width. */
function SpriteSlot({ xPercent, yPx, scale, opacity, children }: SpriteSlotProps) {
  return (
    <div
      style={{
        position: 'absolute',
        left: `${xPercent.toFixed(2)}%`,
        top: yPx,
        transform: `translate(-50%, -50%) scale(${scale.toFixed(2)})`,
        opacity,
      }}
    >
      <svg
        width={140}
        height={140}
        viewBox="-70 -70 140 140"
        style={{ display: 'block', overflow: 'visible' }}
      >
        <defs>
          <PaintDefs />
        </defs>
        {children}
      </svg>
    </div>
  );
}

interface SilhouetteLayerProps {
  idx: number;
  kind: 'far' | 'mid';
  biome: Biome;
  rand: (i: number) => number;
}

function SilhouetteLayer({ idx, kind, biome, rand }: SilhouetteLayerProps) {
  const isFar = kind === 'far';
  const yBase = isFar ? BIOME_HEIGHT_PX * 0.5 : BIOME_HEIGHT_PX * 0.72;
  const amp = isFar ? 60 : 90;
  const freq = isFar ? 8 : 12;
  const seed = isFar ? 0 : 50;
  const fill = isFar
    ? mixColors(biome.sky[0], biome.sky[1], 0.65)
    : mixColors(biome.sky[1], biome.sky[2], 0.45);
  const opacity = isFar ? 0.55 : 0.85;

  let d = `M0 ${BIOME_HEIGHT_PX} L0 ${yBase}`;
  for (let i = 0; i <= freq; i++) {
    const x = (i / freq) * WORLD_PANEL_WIDTH_PX;
    const noise = rand(i + seed) * amp * 0.4;
    const y = yBase - amp * (0.5 + 0.5 * Math.sin(i * 1.7 + freq + seed)) - noise;
    d += ` L${x.toFixed(1)} ${y.toFixed(1)}`;
  }
  d += ` L${WORLD_PANEL_WIDTH_PX} ${BIOME_HEIGHT_PX} Z`;

  return (
    <svg
      width="100%"
      height={BIOME_HEIGHT_PX}
      viewBox={`0 0 ${WORLD_PANEL_WIDTH_PX} ${BIOME_HEIGHT_PX}`}
      preserveAspectRatio="none"
      style={{ position: 'absolute', inset: 0, display: 'block' }}
      aria-hidden
    >
      <path d={d} fill={fill} opacity={opacity} data-layer={`silhouette-${kind}-${idx}`} />
    </svg>
  );
}

function mixColors(a: string, b: string, t: number): string {
  const ah = parseInt(a.slice(1), 16);
  const bh = parseInt(b.slice(1), 16);
  const ar = (ah >> 16) & 255;
  const ag = (ah >> 8) & 255;
  const ab = ah & 255;
  const br = (bh >> 16) & 255;
  const bg = (bh >> 8) & 255;
  const bb = bh & 255;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return `#${((r << 16) | (g << 8) | bl).toString(16).padStart(6, '0')}`;
}
